import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileText, Receipt, FilePlus2, Trash2, Upload, Download,
} from "lucide-react";
import { toast } from "sonner";
import { useFileDrop } from "@/lib/useFileDrop";

const CATEGORY_LABELS = {
  quote:     "Quote",
  variation: "Variation",
  invoice:   "Invoice",
  other:     "Other",
};
const CATEGORY_ICONS = {
  quote:     Receipt,
  variation: FilePlus2,
  invoice:   Receipt,
  other:     FileText,
};
const CATEGORY_ACCENT = {
  quote:     "border-blue-400 bg-blue-50",
  variation: "border-yellow-400 bg-yellow-50",
  invoice:   "border-green-400 bg-green-50",
  other:     "border-zinc-300 bg-zinc-50",
};

/**
 * Job business documents — quotes, variations, invoices, etc.
 * Office/Admin/SuperAdmin only (field workers do NOT see this section at all).
 */
export function JobDocuments({ jobId }) {
  const { user } = useAuth();
  const canManage = user.role !== "field";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("quote");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/documents`);
      setDocs(data);
    } catch (e) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    load();
    // eslint-disable-next-line
  }, [jobId, canManage]);

  const handleFiles = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      await api.post(`/jobs/${jobId}/documents`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${CATEGORY_LABELS[category]} uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const { dragging, dropProps } = useFileDrop(handleFiles, { disabled: busy });

  if (!canManage) return null;  // field workers never see this

  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.filename}?`)) return;
    try {
      await api.delete(`/jobs/${jobId}/documents/${d.id}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const viewUrl = (path) => {
    const tok = localStorage.getItem("fl_token") || "";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(tok)}`;
  };

  return (
    <div className="space-y-3" data-testid={`job-documents-${jobId}`}>
      {/* Upload bar */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="grow max-w-[200px]">
          <Label className="label-tag">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-2 rounded-sm border-zinc-400 h-9"
                           data-testid="job-doc-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className={`flex-1 min-w-[200px] flex items-center justify-center gap-2 h-9 px-3 border-2 border-dashed cursor-pointer text-xs font-bold uppercase tracking-wider transition-colors ${
          busy ? "border-zinc-300 text-zinc-400" : dragging ? "border-[#FF5F15] bg-orange-100 text-[#FF5F15]" : "border-[#FF5F15] text-[#FF5F15] hover:bg-orange-50"
        }`}
               {...dropProps}
               data-testid="job-doc-upload-label">
          {busy ? "Uploading…" : dragging ? "Drop file to upload" : <><Upload className="w-3.5 h-3.5" /> Drag &amp; drop or choose {CATEGORY_LABELS[category].toLowerCase()}</>}
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.csv,.txt"
            className="hidden"
            disabled={busy}
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            data-testid="job-doc-upload-input"
          />
        </label>
      </div>

      {/* Document list */}
      {loading ? (
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-500">
          No quotes, variations or invoices on this job yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="job-doc-list">
          {docs.map((d) => {
            const Icon = CATEGORY_ICONS[d.category] || FileText;
            const accent = CATEGORY_ACCENT[d.category] || CATEGORY_ACCENT.other;
            return (
              <div key={d.id}
                   className={`flex items-center gap-3 border-2 ${accent} p-2.5`}
                   data-testid={`job-doc-row-${d.id}`}>
                <Icon className="w-5 h-5 text-zinc-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-white border border-zinc-300 px-1.5 py-0.5">
                      {CATEGORY_LABELS[d.category] || d.category}
                    </span>
                    <span className="font-mono text-xs font-bold truncate">{d.filename}</span>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                    {(d.size / 1024).toFixed(0)} KB · {d.uploaded_by_name || "—"} · {new Date(d.created_at).toLocaleString("en-NZ")}
                  </div>
                </div>
                <a href={viewUrl(d.storage_path)}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-900 hover:text-[#FF5F15]"
                   data-testid={`job-doc-view-${d.id}`}>
                  <Download className="w-3.5 h-3.5" /> View
                </a>
                <Button size="icon" variant="ghost"
                        onClick={() => remove(d)}
                        className="h-7 w-7 rounded-sm text-zinc-500 hover:text-red-600"
                        data-testid={`job-doc-delete-${d.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
